import { catalogs, fr } from "./messages.js";

export const DEFAULT_LOCALE = "fr";
export const FALLBACK_LOCALE = "fr";
export const SUPPORTED_LOCALES = Object.freeze(["fr", "en"]);
const LOCALE_KEY = "ca-se-prend:locale";

function resolveLocale(candidate) {
  const normalized = String(candidate || "").toLowerCase().split("-")[0];
  return SUPPORTED_LOCALES.includes(normalized) ? normalized : FALLBACK_LOCALE;
}

function initialLocale() {
  const query = new URLSearchParams(globalThis.location?.search || "").get("lang");
  if (query) return resolveLocale(query);
  try {
    const stored = localStorage.getItem(LOCALE_KEY);
    if (stored) return resolveLocale(stored);
  } catch {
    // La préférence n'est simplement pas persistée.
  }
  return resolveLocale(globalThis.navigator?.language || DEFAULT_LOCALE);
}

let activeLocale = initialLocale();

export function locale() {
  return activeLocale;
}

export function number(value, options = {}) {
  return new Intl.NumberFormat(activeLocale, options).format(value);
}

export function date(value, options = {}) {
  return new Intl.DateTimeFormat(activeLocale, options).format(new Date(value));
}

export function t(key, parameters = {}) {
  const catalog = catalogs[activeLocale] || fr;
  const pluralKey = Number.isFinite(Number(parameters.count))
    ? `${key}.${new Intl.PluralRules(activeLocale).select(Number(parameters.count))}`
    : null;
  const template = (pluralKey && (catalog[pluralKey] ?? fr[pluralKey])) ?? catalog[key] ?? fr[key];
  if (typeof template !== "string") return `[${key}]`;
  return template.replaceAll(/\{([A-Za-z0-9_]+)\}/g, (_match, name) => String(parameters[name] ?? `{${name}}`));
}

export function suitName(suit) {
  return t(`suit.${suit}`);
}

export function setLocale(nextLocale) {
  activeLocale = resolveLocale(nextLocale);
  try {
    localStorage.setItem(LOCALE_KEY, activeLocale);
  } catch {
    // La langue reste active pour la session.
  }
  if (typeof document !== "undefined") {
    document.documentElement.lang = activeLocale;
    translateDocument();
  }
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("app-locale-changed", { detail: { locale: activeLocale } }));
}

export function translateDocument(root = globalThis.document) {
  if (!root || typeof document === "undefined") return;
  document.documentElement.lang = activeLocale;
  document.title = t("meta.title");
  document.querySelector('meta[name="description"]')?.setAttribute("content", t("meta.description"));
  root.querySelectorAll("[data-i18n]").forEach((element) => {
    const target = /** @type {HTMLElement} */ (element);
    target.textContent = t(target.dataset.i18n);
  });
  root.querySelectorAll("[data-i18n-html]").forEach((element) => {
    const target = /** @type {HTMLElement} */ (element);
    target.innerHTML = t(target.dataset.i18nHtml);
  });
  root.querySelectorAll("[data-i18n-aria-label]").forEach((element) => {
    const target = /** @type {HTMLElement} */ (element);
    target.setAttribute("aria-label", t(target.dataset.i18nAriaLabel));
  });
  const selector = /** @type {HTMLSelectElement | null} */ (root.querySelector("#locale-select"));
  if (selector) selector.value = activeLocale;
}

export function initI18n() {
  if (typeof document === "undefined") return;
  translateDocument();
  document.querySelector("#locale-select")?.addEventListener("change", (event) => {
    const selector = /** @type {HTMLSelectElement} */ (event.currentTarget);
    setLocale(selector.value);
  });
}

export function pseudoCatalog(catalog = fr) {
  return Object.fromEntries(Object.entries(catalog).map(([key, value]) => [
    key,
    `⟦${value.split(/(\{[A-Za-z0-9_]+\})/g).map((part) => (
      /^\{[A-Za-z0-9_]+\}$/.test(part)
        ? part
        : part.replace(/[aeiouyàâéèêëîïôùûü]/gi, (character) => `${character}${character}`)
    )).join("")}⟧`,
  ]));
}
